/**
 * KpiWizardLauncher (DEV-841 / DEV-840 — Path A).
 *
 * Mounts at the project-detail level. Listens for `l4e_open_kpi_config` window
 * events dispatched by thermometer pencil buttons (Schedule / Budget / future
 * Mission cards) and opens the appropriate KPI configuration wizard in a
 * Tray — DIRECTLY, with no intermediate ProjectConfigModal hop.
 *
 * Architecture decision (Path A): the wizard is now a self-contained edit
 * surface with its own save path. Previous behavior funneled the pencil
 * through ProjectConfigModal, which meant:
 *   1. The big modal background flashed open before the wizard tray slid in.
 *   2. Saving the wizard only mutated draft state — user had to click Save in
 *      the modal too, which produced "I saved but it didn't save" confusion.
 *
 * This launcher fixes both:
 *   1. No modal hop. Wizard tray opens directly over the page.
 *   2. Save commits via upsertProjectKpiConfigOverride — same persist path
 *      ProjectConfigModal uses on its own Save — but for the single sub-KPI
 *      the user is editing. No batched-modal commit, no transaction surprise.
 *
 * The in-modal flow (ProjectConfigModal → "+ Add" / pencil on a row) still
 * works as before — those wizards mount inside the modal and stage drafts
 * for the modal's atomic Save. Two consumers, one wizard component, two
 * save handlers. Save behavior matches user expectation for each context.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Tray } from '../Tray/Tray';
import { ScheduleKpiWizard } from '../ProjectConfigModal/ScheduleKpiWizard';
import {
  getProjectKpiConfigOverride,
  upsertProjectKpiConfigOverride,
  type SubKpiInstance,
} from '../../Data/ProjectConfig/projectKpiConfigOverrides';
import { guidEquals } from '../../utils/guid';

/* ── Event payload contract ────────────────────────────────── */

interface OpenKpiConfigDetail {
  category?: string;     // 'schedule' | 'finance' | 'mission' | 'data_reliability'
  projectGuid?: string;  // identifies which project's override to edit
  instanceGuid?: string; // when present, opens in edit mode for that sub-KPI
}

/* ── Component props ───────────────────────────────────────── */

export interface KpiWizardLauncherProps {
  /**
   * The current page's project guid. The launcher only acts on events whose
   * `projectGuid` matches this prop — a safety check so an event from a
   * stale background tab can't mutate the wrong project.
   */
  projectGuid: string;
  /**
   * Optional callback fired after a successful save so the parent can
   * re-resolve hero images, refetch project data, etc. Mirrors the
   * `onSaved` callback ProjectConfigModal already uses.
   */
  onSaved?: () => void;
}

interface LauncherState {
  open: boolean;
  category: string | null;
  instanceGuid: string | null;
}

const INITIAL: LauncherState = { open: false, category: null, instanceGuid: null };

/* ── Component ─────────────────────────────────────────────── */

export function KpiWizardLauncher({ projectGuid, onSaved }: KpiWizardLauncherProps) {
  const [state, setState] = useState<LauncherState>(INITIAL);

  const close = useCallback(() => setState(INITIAL), []);

  // Event listener — open the launcher when a thermometer pencil dispatches.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as OpenKpiConfigDetail | undefined;
      if (!detail?.category) return;
      // Project guid scoping: ignore events for other projects (e.g. from a
      // stale tab in the background, or from an unrelated detail surface).
      if (detail.projectGuid && projectGuid && !guidEquals(detail.projectGuid, projectGuid)) {
        return;
      }
      setState({
        open: true,
        category: detail.category,
        instanceGuid: detail.instanceGuid ?? null,
      });
    };
    window.addEventListener('l4e_open_kpi_config', handler as EventListener);
    return () => window.removeEventListener('l4e_open_kpi_config', handler as EventListener);
  }, [projectGuid]);

  // Resolve the existing instance for edit mode (if instanceGuid was provided).
  const existingInstance: SubKpiInstance | undefined = (() => {
    if (!state.instanceGuid || !state.category) return undefined;
    const ov = getProjectKpiConfigOverride(projectGuid as any);
    const list = (ov?.category_instances as any)?.[state.category] || [];
    return list.find((x: SubKpiInstance) => guidEquals(x.instance_guid, state.instanceGuid!));
  })();

  /**
   * Direct-save handler. Reads the full override, surgically replaces (or
   * appends) the single sub-KPI, writes the whole thing back. This matches
   * the persist path ProjectConfigModal uses on its Save click — same
   * function, same shape — so server-side handling is identical.
   *
   * Mandatory-instance protections preserved: when editing an existing
   * mandatory KPI, catalog_kpi_guid and is_mandatory survive the wizard.
   */
  const handleSave = useCallback(
    (instance: SubKpiInstance) => {
      if (!state.category) return;
      const ov = getProjectKpiConfigOverride(projectGuid as any) || {} as any;
      const currentCategoryInstances = ov.category_instances || {};
      const currentList: SubKpiInstance[] = (currentCategoryInstances as any)[state.category] || [];

      const idx = currentList.findIndex((x) => guidEquals(x.instance_guid, instance.instance_guid));
      let nextList: SubKpiInstance[];
      if (idx >= 0) {
        const prior = currentList[idx];
        const merged: SubKpiInstance = {
          ...instance,
          is_mandatory: prior.is_mandatory,
          weight: instance.weight ?? prior.weight,
        };
        if (prior.is_mandatory) {
          // Mandatory KPI — catalog_kpi_guid is locked.
          merged.catalog_kpi_guid = String(prior.catalog_kpi_guid);
        }
        nextList = [...currentList];
        nextList[idx] = merged;
      } else {
        // Add path — auto-weight only when there's an existing list to balance against.
        const newInstance = { ...instance };
        if (currentList.length === 0) newInstance.weight = 100;
        nextList = [...currentList, newInstance];
      }

      try {
        upsertProjectKpiConfigOverride({
          project_guid: projectGuid as any,
          kpi_profile_guid: ov.kpi_profile_guid,
          category_weights: ov.category_weights,
          category_instances: {
            ...currentCategoryInstances,
            [state.category]: nextList,
          } as any,
        } as any);
      } catch (err) {
        console.error('[KpiWizardLauncher] save failed:', err);
      }

      close();
      onSaved?.();
    },
    [state.category, projectGuid, onSaved, close],
  );

  // No wizard is registered for this category yet — close silently.
  // Mission and Data Reliability launches will land here until they wire up.
  if (state.open && state.category && state.category !== 'schedule' && state.category !== 'finance') {
    console.warn(`[KpiWizardLauncher] no wizard registered for category=${state.category}`);
  }

  if (!state.open) return null;

  // Wizard selection. Schedule and Finance both currently use ScheduleKpiWizard
  // because Budget hasn't gotten its own wizard yet (sibling DEV ticket).
  // When BudgetKpiWizard ships, swap in the right component on category.
  const wizard =
    state.category === 'schedule' || state.category === 'finance' ? (
      <ScheduleKpiWizard
        mode={existingInstance ? 'edit' : 'add'}
        existingInstance={existingInstance}
        projectGuid={projectGuid}
        onSave={handleSave}
        onCancel={close}
      />
    ) : (
      // Future categories — render a placeholder that closes itself so the
      // tray doesn't dead-end. Better: surface a "not yet implemented" notice.
      <div style={{ padding: 24 }}>
        <p>No wizard registered for category "{state.category}". This is a launcher gap, not a user error.</p>
        <button onClick={close}>Close</button>
      </div>
    );

  return (
    <Tray open={true} onClose={close} width={560}>
      {wizard}
    </Tray>
  );
}

export default KpiWizardLauncher;
